// API Types
export interface User {
  username: string;
  email: string;
  name: string;
  roles: string[];
  groups: string[];
  is_authenticated: boolean;
}

// Role hierarchy (same as Streamlit app)
export const ROLE_HIERARCHY = {
  administrator: 5,
  user_access_administrator: 4,
  analyst: 3,
  viewer: 2,
  guest: 1
} as const;

export type UserRole = keyof typeof ROLE_HIERARCHY;

// Azure AD Authentication
export interface AzureAuthResponse {
  access_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in?: number;
  user?: User;
}

export interface AuthUrlResponse {
  auth_url: string;
}

// Geographic Access Control
export interface GeographicRegion {
  name: string;
  description?: string;
  bbox?: [number, number, number, number]; // [min_lat, min_lon, max_lat, max_lon]
  country_codes?: string[];
}

export interface GeographicAccessResponse {
  enabled: boolean;
  worldwide_access: boolean;
  allowed_regions: GeographicRegion[];
  max_query_radius_meters: number;
  max_date_range_days: number;
  group_name?: string;
  description?: string;
  message?: string;
}

// Permission check utilities
export function checkPermission(userRoles: string[], requiredRole: UserRole): boolean {
  const userLevel = Math.max(...userRoles.map(role => ROLE_HIERARCHY[role as UserRole] || 0));
  const requiredLevel = ROLE_HIERARCHY[requiredRole];
  return userLevel >= requiredLevel;
}

export function getUserHighestRole(userRoles: string[]): UserRole | null {
  const roleHierarchy = Object.entries(ROLE_HIERARCHY);
  const userLevels = userRoles.map(role => ROLE_HIERARCHY[role as UserRole] || 0);
  const highestLevel = Math.max(...userLevels);
  
  const highestRole = roleHierarchy.find(([, level]) => level === highestLevel);
  return highestRole ? (highestRole[0] as UserRole) : null;
}

// Dataset Types
export enum DeviceType {
  VEHICLE = 'Vehicle',
  PHONE = 'Phone',
  WATCH = 'Watch',
}

export enum DatasetStatus {
  UPLOADED = 'uploaded',
  PROCESSING = 'processing',
  PROCESSED = 'processed',
  ERROR = 'error',
}

export interface Dataset {
  id: number;
  name: string;
  description?: string;
  device_type: DeviceType;
  status: DatasetStatus;
  original_filename?: string;
  file_size?: number;
  total_points?: number;
  date_range_start?: string;
  date_range_end?: string;
  students?: string[];
  uploaded_at: string;
  processed_at?: string;
  error_message?: string;
}

export interface UploadResponse {
  dataset_id: number;
  message: string;
  status: string;
}

// Analysis Types
export enum AnalysisType {
  QUICK = 'quick',
  FULL = 'full',
  ROUTE_ONLY = 'route_only',
  TEMPORAL_ONLY = 'temporal_only',
  MULTI_DEVICE = 'multi_device',
}

export enum JobStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

export interface AnalysisParameters {
  enable_dwell_detection: boolean;
  enable_dwell_based_routes: boolean;
  enable_non_dwell_routes: boolean;
  enable_temporal_analysis: boolean;
  enable_connectivity_analysis: boolean;
  enable_route_consistency: boolean;
  enable_place_lookup: boolean;
  enable_ai_insights: boolean;
  dwell_radius: number;
  stay_point_time_threshold: number;
  stay_point_distance_threshold: number;
  min_route_length: number;
  min_segment_distance: number;
  route_similarity_threshold: number;
  max_dwell_locations: number;
  max_routes: number;
}

export interface AnalysisRequest {
  name?: string;
  analysis_type: AnalysisType;
  device_type: DeviceType | null; // null for multi-device analysis
  dataset_id?: number; // Optional when using ArcGIS
  course_id?: string; // ArcGIS course ID
  device_ids?: string[]; // Watchlist device registration IDs
  students?: string[];
  date_range_start?: string;
  date_range_end?: string;
  parameters?: AnalysisParameters;
  notification_email?: string; // Email for completion notification
}

export interface AnalysisJob {
  id: number;
  name?: string;
  analysis_type?: string;
  status: JobStatus;
  progress_percentage: number;
  current_step?: string;
  created_at?: string;
  estimated_completion?: string;
  start_time: string;
  end_time?: string;
  error_message?: string;
  error_traceback?: string;
  dataset_id?: number;
  course_id?: string;
  device_ids?: string[];  // Watchlist device registration IDs
  students: string[];
  date_range_start: string;
  date_range_end: string;
  parameters?: AnalysisParameters;
  results_summary?: Record<string, any>;
  processing_duration?: number;
  dwell_locations?: DwellLocation[];
  routes?: Route[];
  output_files?: string[];
  user_id?: string;  // User who created the job
}

export interface AnalysisProgress {
  job_id: number;
  status: JobStatus;
  progress_percentage: number;
  current_step: string;
  message?: string;
  estimated_completion?: string;
}

// Results Types
// Enhanced visit record structure for temporal analysis
export interface VisitRecord {
  start_time: string;
  end_time?: string | null;
  duration_seconds?: number;
  entity_id: string;
}

export interface DwellLocation {
  id: number;
  center_latitude: number;
  center_longitude: number;
  radius?: number;
  total_visits?: number;
  total_duration?: number;
  average_duration?: number;
  max_duration?: number;
  place_name?: string;
  place_type?: string;
  address?: string;
  confidence_score?: number;  // Geocoding confidence (0.0-1.0)
  metadata?: any; // Geocoding metadata (name, address, etc.)
  students?: string[];
  visit_times?: string[] | VisitRecord[]; // Support both old (string[]) and new (VisitRecord[]) formats
  time_of_day_pattern?: { [hour: string]: number }; // Hourly visit counts
  day_of_week_pattern?: { [day: string]: number }; // Daily visit counts
  device_type?: string; // Device type for multi-device analysis
}

export interface Route {
  id: number;
  route_name?: string;
  path_coordinates: number[][];
  start_location?: { [key: string]: any };
  end_location?: { [key: string]: any };
  distance?: number;
  average_duration?: number;
  frequency?: number;
  students?: string[];
  consistency_score?: number;
  device_type?: string; // Device type for multi-device analysis
}

export interface AnalysisResults {
  job_id: number;
  analysis_type: string;
  status: string;
  summary_stats?: { [key: string]: any };
  dwell_locations: DwellLocation[];
  routes: Route[];
  reports: string[];
  processing_duration?: number;
  completed_at?: string;
}

// Report Types
export enum ReportType {
  HTML_INTERACTIVE = 'html_interactive',
  PDF_SUMMARY = 'pdf_summary',
  CSV_DATA = 'csv_data',
  JSON_DATA = 'json_data',
  MAP_EXPORT = 'map_export',
}

export interface Report {
  id: number;
  title?: string;
  report_type: string;
  file_path: string;
  file_size?: number;
  summary_stats?: { [key: string]: any };
  is_public: boolean;
  download_count: number;
  generated_at: string;
  expires_at?: string;
}

// WebSocket Types
export interface WebSocketMessage {
  type: string;
  job_id?: number;
  timestamp?: string;
  [key: string]: any;
}

// UI Types
export interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

export interface NotificationState {
  open: boolean;
  message: string;
  severity: 'error' | 'warning' | 'info' | 'success';
}

// Job Sharing Types
export interface JobShare {
  id: number;
  shared_with_upn?: string | null;
  shared_with_org_id?: number | null;
  // Resolved display name: the person's Graph displayName, or the organization name
  shared_with_name?: string | null;
  created_at?: string | null;
}

export interface ShareRecipientUser {
  upn: string;
  display_name: string;
  email?: string | null;
}

export interface ShareRecipientOrg {
  id: number;
  name: string;
}

export interface ShareRecipients {
  users: ShareRecipientUser[];
  organizations: ShareRecipientOrg[];
}

// Export scheduled analysis types
export * from './scheduled';